import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// components
import { CButton, CImage } from '@coreui/react-pro'
import AppCard from '../../components/AppCard'
import AppCardList from '../../components/AppCardList'
import AppTable from 'src/components/AppTable'
import AppModalConfirmation from 'src/components/modal/AppModalConfirmation'
import AppModalImportExcel from 'src/components/modal/AppModalImportExcel'

// icons
import AddMoreIconWhite from '../../assets/icons/AddMoreIconWhite.png'

// styling
import { styleButtonVars2 } from '../../components/custom-styles/buttonStyles'

// helpers
import Axios from '../../helpers/axios'
import { columnProjectList } from '../../components/table-column/TableColumnList'
import { cardProjectList } from '../../components/card-field/CardFieldList'
import { formUrlQuery } from '../../helpers/format-queries'
import useToastMessage from 'src/helpers/toast-message'
import { useResponsive } from 'src/helpers/get-responsive'

const ProjectList = () => {
  const isDesktop = useResponsive();
  const navigate = useNavigate()
  const { showSuccess, showError } = useToastMessage();

  const [showConfirmDelete, setShowConfirmDelete] = useState({ show: false, item: {} })
  const [showImportModal, setShowImportModal] = useState(false)

  const [projectList, setProjectList] = useState([]);
  const [projectListCount, setProjectListCount] = useState(0);
  const [projectListPerPage, setProjectListPerPage] = useState(10);
  const [activeProjectListPage, setActiveProjectListPage] = useState(1);
  const [searchProjectListFilter, setSearchProjectListFilter] = useState("");
  const [columnProjectListSorter, setColumnProjectListSorter] = useState({ column: "createdAt", state: "desc" });

  useEffect(() => {
    let queries = {
      search: searchProjectListFilter,
      limit: projectListPerPage,
      offset: projectListPerPage * activeProjectListPage - projectListPerPage,
      sort:
        (columnProjectListSorter &&
          `${columnProjectListSorter.column}%${columnProjectListSorter.state}`) ||
        "",
    };
    queries = formUrlQuery(queries);
    fetchAllProjects(queries);
  }, [
    searchProjectListFilter,
    projectListPerPage,
    activeProjectListPage,
    columnProjectListSorter,
  ]);

  const handleSearchItems = (search) => {
    setActiveProjectListPage(1)
    setSearchProjectListFilter(search);
  }

  const fetchAllProjects = (queries) => {
    Axios({
      method: "get",
      url: `/api/tracker/v1/project?${queries}`
    })
      .then(({ data }) => {
        const tempProjectList = data?.value?.map((d) => ({
          ...d,
          client_name: d?.client?.name
        }));
        setProjectList(tempProjectList);
        if (activeProjectListPage === 1) {
          setProjectListCount(data?.count);
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };

  const handleDeleteConfirmation = () => {
    Axios({
      method: "delete",
      url: `/api/tracker/v1/project/delete/${showConfirmDelete?.item?.id}`,
    })
      .then(({ data }) => {
        if (data?.success) {
          setShowConfirmDelete({ show: false, item: {} })
          showSuccess('Project has been successfully deleted')
          fetchAllProjects()
          window.location.reload()
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const handleOnRowClick = (item, columnName) => {
    if (columnName !== 'action') {
      navigate(`/project/details/${item.id}`)
    }
  }

  const handleImportProject = async (file) => {
    const formData = new FormData()
    formData.append('file', file)

    try {
      const { data } = await Axios({
        method: 'post',
        url: '/api/tracker/v1/project/import',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (data?.success) {
        showSuccess('Project import success')
        setActiveProjectListPage(1)
        let queries = {
          search: searchProjectListFilter,
          limit: projectListPerPage,
          offset: 0,
          sort:
            (columnProjectListSorter &&
              `${columnProjectListSorter.column}%${columnProjectListSorter.state}`) ||
            ''
        }
        queries = formUrlQuery(queries)
        fetchAllProjects(queries)
      }
    } catch (err) {
      console.error(err)
      const errorMessage = err?.response?.data?.errors?.[0]?.reason || err?.response?.data?.message
      showError(errorMessage)
      throw err
    }
  }

  return (
    <>
      {
        showConfirmDelete?.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={showConfirmDelete?.item?.name}
            itemDetails={showConfirmDelete.item}
            itemType={showConfirmDelete.type}
            itemIndex={showConfirmDelete?.index}
            showModal={showConfirmDelete}
            setShowModal={setShowConfirmDelete}
            handleConfirmation={handleDeleteConfirmation}
          />
        )
      }
      <AppModalImportExcel
        showModal={showImportModal}
        setShowModal={setShowImportModal}
        modalTitle='Import Project'
        templateUrl='/api/tracker/v1/project/import/template'
        previewUrl='/api/tracker/v1/project/import/preview'
        defaultFileName='project-list.xlsx'
        handleSubmit={handleImportProject}
      />
      <div className='d-grid gap-2 d-flex justify-content-end mb-3'>
        <CButton color='dark' variant='outline' onClick={() => setShowImportModal(true)}>
          Import
        </CButton>
        <CButton style={styleButtonVars2} onClick={() => navigate('/project/add')}>
          <CImage fluid src={AddMoreIconWhite} className='me-2' />
          Add Project
        </CButton>
      </div>
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Project List</h4>}
        bodyContent={
          isDesktop ? (
            <AppTable
              projectListPage
              tableFilter
              setShowConfirmDelete={setShowConfirmDelete}
              tableColumns={columnProjectList}
              tableContent={projectList}
              contentPerPage={projectListPerPage}
              activeContentPage={activeProjectListPage}
              contentCount={projectListCount}
              searchContentFilter={searchProjectListFilter}
              setActiveContentPage={setActiveProjectListPage}
              setSearchContentFilter={handleSearchItems}
              setContentPerPage={setProjectListPerPage}
              setColumnContentSorter={setColumnProjectListSorter}
              handleOnRowClick={handleOnRowClick}
              isClickable={true}
            />
          ) : (
            <AppCardList
              items={projectList}
              cardFields={cardProjectList}
              titleKey='name'
              subtitleKey='client_name'
              subtitlePrefix='Client: '
              isTitleCapitalized={true}
              actions={[
                {
                  label: 'Edit Project',
                  onClick: (item) => navigate(`/project/edit/${item.id}`)
                },
                {
                  label: 'Delete Project',
                  onClick: (item) => setShowConfirmDelete({ show: true, type: 'project', item })
                },
              ]}
              onItemClick={handleOnRowClick}
              searchValue={searchProjectListFilter}
              onSearch={handleSearchItems}
              activePage={activeProjectListPage}
              contentPerPage={projectListPerPage}
              contentCount={projectListCount}
              onPageChange={setActiveProjectListPage}
            />
          )
        }
      />
    </>
  )
}

export default ProjectList